import React, { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'reactstrap';
import { RegisteredInputsBuilder, ModalButtonBuilder, ToastBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, sortArrayByKey, loadObjectToTextArea, parseJSONSafe } from '../../../../../../../../../GlobalComponents/Helpers';
import { ProgressLoader } from '../../../../../../../../../Layout/Loader';

const AddResourceForms = ({ connectionData, availableResources, fetchAvailableResources, jobData, jobResources, isAdmin, onResourceCreate, connectionType = 'Source' }) => {

    const addForm = useForm();
    const createForm = useForm();
    const [loadingResources, setLoadingResources] = useState(false);
    const [parentHasSetupIds, setParentHasSetupIds] = useState(false);
    const [parentSetupIds, setParentSetupIds] = useState([]);
    const [parentResourceName, setParentResourceName] = useState('');
    const [autoResolvedUsage, setAutoResolvedUsage] = useState('Key Mapping');

    const ensureResourcesLoaded = async () => {
        if (availableResources[connectionData.id]) return;
        setLoadingResources(true);
        try {
            await fetchAvailableResources();
        } finally {
            setLoadingResources(false);
        }
    };

    const resourceOptionsPlaceholder = '{"setup_ids":[1,3]}\n{"parent_resource_ids":[20]}\n{"args":{"conditions":"id = 456"}}';
    const resourceOptionsHelpText = <span>{'{ "setup_ids" : [X] }'}<br />{'{ "parent_resource_ids" : [X] }'}<br />{'{"args" : { "conditions": "id = X" } }'}</span>;

    /**
     * When a resource is selected, check if it has a parent with setup_ids.
     * If so, enable the "Use parent IDs?" dropdown and optionally call onFound callback.
     * Checks jobResources (job-specific resource_options) first, then availableResources.
     */
    const checkParentSetupIds = (selectedResource, onFound = null) => {
        if (!selectedResource?.parent_resource_id) {
            setParentHasSetupIds(false);
            setParentSetupIds([]);
            setParentResourceName('');
            return;
        }

        // First check job-level resources (where setup_ids lives)
        const selectedResources = jobResources[connectionData.id]?.selected_resources || [];
        let parentResource = selectedResources.find(r => parseInt(r.resource_id) === parseInt(selectedResource.parent_resource_id));

        // Fall back to available resources
        if (!parentResource) {
            const resources = availableResources[connectionData.id]?.resources || [];
            parentResource = resources.find(r => parseInt(r.resource_id) === parseInt(selectedResource.parent_resource_id));
        }

        if (!parentResource) {
            setParentHasSetupIds(false);
            setParentSetupIds([]);
            setParentResourceName('');
            return;
        }

        const parentOptions = parseJSONSafe(parentResource.resource_options);
        if (parentOptions && Array.isArray(parentOptions.setup_ids) && parentOptions.setup_ids.length > 0) {
            setParentHasSetupIds(true);
            setParentSetupIds(parentOptions.setup_ids);
            setParentResourceName(parentResource.name);
            if (onFound) onFound(parentOptions.setup_ids, parentResource.name);
        } else {
            setParentHasSetupIds(false);
            setParentSetupIds([]);
            setParentResourceName('');
        }
    };

    /**
     * When user selects "Yes" on the Use Parent IDs dropdown,
     * merge parent's setup_ids into the child's resource_options.parent_resource_ids
     */
    const handleUseParentIds = (useParent, formInstance, fieldName, setupIdsOverride = null, parentNameOverride = null) => {
        const ids = setupIdsOverride || parentSetupIds;
        const name = parentNameOverride || parentResourceName;

        const currentOptionsRaw = formInstance.getValues(fieldName);
        let currentOptions = parseJSONSafe(currentOptionsRaw) || {};

        if (useParent === 'yes') {
            currentOptions.parent_resource_ids = [...ids];
            const newValue = loadObjectToTextArea(currentOptions);
            formInstance.setValue(fieldName, newValue, { shouldDirty: true, shouldValidate: true });
            // Force DOM update for uncontrolled textarea
            setTimeout(() => {
                const el = document.querySelector(`textarea[name="${fieldName}"]`);
                if (el) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
                    nativeInputValueSetter.call(el, newValue);
                    el.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 0);
            ToastBuilder('success', `Loaded ${ids.length} parent IDs from ${name}`);
        } else if (useParent === 'no') {
            currentOptions.parent_resource_ids = [];
            const newValue = loadObjectToTextArea(currentOptions);
            formInstance.setValue(fieldName, newValue, { shouldDirty: true, shouldValidate: true });
            setTimeout(() => {
                const el = document.querySelector(`textarea[name="${fieldName}"]`);
                if (el) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
                    nativeInputValueSetter.call(el, newValue);
                    el.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 0);
        }
    };

    return (
        <Fragment>
            {/* add resource */}
            <ModalButtonBuilder title='Add Resource' className='btn-primary px-3' formIsValid={addForm.formState.isValid} formId={`add-resource-form-${connectionData.id}`}
                style={{maxWidth: 550}}
                buttonStyle={{}}
                onClick={ensureResourcesLoaded}
                body={
                    loadingResources ? <ProgressLoader /> :
                    <Form id={`add-resource-form-${connectionData.id}`} onSubmit={addForm.handleSubmit((update) => onResourceCreate(update, String(connectionData.id), false, { resetField: addForm.resetField, setValue: addForm.setValue }))} style={{ paddingTop: '10px' }}>
                        <RegisteredInputsBuilder
                            registrator={addForm.register}
                            registratorErrors={addForm.formState.errors}
                            control={addForm.control}
                            inputs={[
                                {
                                    label: 'Resource',
                                    type: 'reactselect',
                                    labelSize: 3,
                                    required: true,
                                    requiredInput: true,
                                    options: buildSelectOptionsFromArray([{ name: 'Select a resource...', resource_id: '' }].concat(availableResources[connectionData.id] ? sortArrayByKey(availableResources[connectionData.id].resources.filter(resource => (resource.partner_id === null || resource.partner_id === jobData.partner_id) && !(jobResources[connectionData.id]?.selected_resources || []).some(r => parseInt(r.resource_id) === parseInt(resource.resource_id))), 'name') : []), { label: 'name', value: 'resource_id' }),
                                    name: 'resource' + String(connectionData.id),
                                    onChange: (e) => {
                                        const selectedResource = availableResources[connectionData.id]?.resources.find(r => r.resource_id === parseInt(e.target.value));
                                        if (selectedResource?.resource_options) {
                                            addForm.setValue('resourceOptions' + String(connectionData.id), loadObjectToTextArea(selectedResource.resource_options));
                                        } else {
                                            addForm.setValue('resourceOptions' + String(connectionData.id), '');
                                        }
                                        // Default resource usage based on service_resource.is_setup_resource
                                        const resolvedUsage = selectedResource?.is_setup_resource ? 'Setup Tables' : 'Key Mapping';
                                        setAutoResolvedUsage(resolvedUsage);
                                        addForm.setValue('resourceUsage' + String(connectionData.id), 'default');
                                        checkParentSetupIds(selectedResource, (setupIds, parentName) => {
                                            addForm.setValue('useParentIds' + String(connectionData.id), 'yes');
                                            handleUseParentIds('yes', addForm, 'resourceOptions' + String(connectionData.id), setupIds, parentName);
                                        });
                                    }
                                },
                                {
                                    label: 'Resource Usage',
                                    type: 'reactselect',
                                    labelSize: 4,
                                    inputSize: 7,
                                    required: false,
                                    options: [{ label: `Default — ${autoResolvedUsage}`, value: 'default' }, { label: 'Key Mapping', value: 'key' }, { label: 'Setup Tables', value: 'setup' }, { label: 'Supplementary Data', value: 'supplementary' }],
                                    name: 'resourceUsage' + String(connectionData.id),
                                    helpText: 'How is this resource used on this job?',
                                    settings: { helpTextOffset: 1 },
                                },
                                {
                                    label: 'Use Parent IDs?',
                                    type: 'reactselect',
                                    labelSize: 4,
                                    inputSize: 7,
                                    required: false,
                                    options: [{ label: `Yes — load from ${parentResourceName}`, value: 'yes' }, { label: 'No', value: 'no' }],
                                    name: 'useParentIds' + String(connectionData.id),
                                    visible: parentHasSetupIds,
                                    helpText: parentSetupIds.length > 5 ? `Parent has ${parentSetupIds.length} setup IDs` : `Parent has ${parentSetupIds.length} setup IDs: [${parentSetupIds.join(', ')}]`,
                                    settings: { helpTextOffset: 1 },
                                    onChange: (e) => {
                                        handleUseParentIds(e.target.value, addForm, 'resourceOptions' + String(connectionData.id));
                                    }
                                },
                                {
                                    label: 'Resource Options',
                                    type: 'textarea',
                                    required: false,
                                    name: 'resourceOptions' + String(connectionData.id),
                                    visible: (isAdmin),
                                    placeholder: resourceOptionsPlaceholder,
                                    helpText: resourceOptionsHelpText,
                                    settings: { handleTabs: true, resize: true, customTextArea: true },
                                    style: { height: 120, width: '130%', maxWidth: '130%' },
                                },
                            ]} />
                    </Form>
                }
            />
            {/* create resource */}
            <ModalButtonBuilder title='Create Resource' className='btn-secondary px-3' formIsValid={createForm.formState.isValid} formId={`create-resource-form-${connectionData.id}`} formErrors={createForm.formState.errors}
                style={{maxWidth: 550}}
                body={
                    <Fragment>
                        <div>
                            <p>Some resources and their values need to be created manually if the connection doesn't have an endpoint to retrieve specific mapping information. <br /> After creating a new resource, add the destination resource that it will be mapped to. <br /> They will create the baseline mappings for which you can then edit down below in the mapping tables.</p>
                        </div>
                        <br />
                        <Form id={`create-resource-form-${connectionData.id}`} onSubmit={createForm.handleSubmit((update) => onResourceCreate(update, connectionData, true, { resetField: createForm.resetField, setValue: createForm.setValue }))}>
                            <RegisteredInputsBuilder
                                registrator={createForm.register}
                                registratorErrors={createForm.formState.errors}
                                control={createForm.control}
                                inputs={[
                                    {
                                        label: 'Name',
                                        inputSize: 7,
                                        name: 'newResourceName',
                                        required: true,
                                    },
                                    {
                                        label: 'Resource',
                                        inputSize: 7,
                                        name: 'newResource',
                                        helpText: connectionData.service_code === 'csv' ? 'e.g., Configuration, AgreementAssociation' : 'API Endpoint (required for standard mapping resources)',
                                        required: connectionData.service_code === 'csv' ? false : (createForm.watch('newResourceUsage') === '' || !createForm.watch('newResourceUsage')),
                                        placeholder: connectionData.service_code === 'csv' ? 'e.g., Configuration' : 'e.g., company/configurations',
                                    },
                                    {
                                        label: 'Resource Usage',
                                        type: 'reactselect',
                                        inputSize: 7,
                                        required: false,
                                        options: [{ label: 'Default — Setup Tables', value: 'default' }, { label: 'Key Mapping', value: 'key' }, { label: 'Setup Tables', value: 'setup' }, { label: 'Supplementary Data', value: 'supplementary' }],
                                        name: 'newResourceUsage',
                                        helpText: 'How is this resource used on this job?',
                                        settings: { helpTextOffset: 1 },
                                    },
                                    {
                                        label: 'Resource Options',
                                        type: 'textarea',
                                        required: false,
                                        name: 'newResourceOptions',
                                        visible: (isAdmin),
                                        placeholder: resourceOptionsPlaceholder,
                                        helpText: resourceOptionsHelpText,
                                        settings: { handleTabs: true, resize: true, customTextArea: true },
                                        style: { height: 120, width: '130%', maxWidth: '130%' },
                                    },
                                ]} />
                        </Form>
                    </Fragment>
                }
            />
        </Fragment>
    );
};

export default AddResourceForms;
